// Step 13 — DealActionDetail modal.
//
// Read + human-review surface for one DealAction.
// Controls are conditioned on the current status; invalid verbs are
// hidden entirely (the backend also refuses them). No "Send"/"Contact
// buyer" verbs — Step 13 does not ship autonomous communication.

import { useCallback, useEffect, useState } from "react";

import {
  addDealActionComment,
  approveDealAction,
  blockDealAction,
  cancelDealAction,
  completeDealAction,
  getDealAction,
  getDealActionHistory,
  listDealActionComments,
  rejectDealAction,
  resolveDealActionBlockers,
  sendDealActionForReview,
  startDealAction,
  supersedeDealAction,
} from "./api";

export default function DealActionDetail({
  workspaceId, actionId, onClose, onChanged,
}) {
  const [action, setAction] = useState(null);
  const [history, setHistory] = useState([]);
  const [comments, setComments] = useState([]);
  const [error, setError] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");

  const reload = useCallback(async () => {
    if (!workspaceId || !actionId) return;
    setError(null);
    try {
      const [a, h, cs] = await Promise.all([
        getDealAction(workspaceId, actionId),
        getDealActionHistory(workspaceId, actionId),
        listDealActionComments(workspaceId, actionId),
      ]);
      setAction(a);
      setHistory(h?.results || []);
      setComments(cs?.results || []);
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte ladda åtgärden.");
    }
  }, [workspaceId, actionId]);

  useEffect(() => { reload(); }, [reload]);

  const runVerb = useCallback(
    async (fn, args) => {
      setError(null);
      try {
        await fn(workspaceId, actionId, args || {});
        await reload();
        onChanged?.();
      } catch (e) {
        setError(e?.body?.detail || e.message || "Åtgärden misslyckades.");
      }
    },
    [workspaceId, actionId, reload, onChanged],
  );

  const submitComment = useCallback(async () => {
    const body = commentDraft.trim();
    if (!body) return;
    try {
      await addDealActionComment(workspaceId, actionId, body);
      setCommentDraft("");
      await reload();
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte spara kommentar.");
    }
  }, [workspaceId, actionId, commentDraft, reload]);

  if (!actionId) return null;

  return (
    <div className="qai-da-detail-backdrop" onClick={onClose}>
      <div
        className="qai-da-detail"
        role="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="qai-da-detail-header">
          <div className="qai-da-detail-title">
            {action?.title || "Laddar…"}
          </div>
          <button className="qai-da-detail-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="qai-da-error">{error}</div>}

        {action && (
          <>
            <MetaGrid a={action} />

            {action.description && (
              <Section title="Beskrivning">
                <div className="qai-da-detail-text">{action.description}</div>
              </Section>
            )}

            {action.rationale && (
              <Section title="Motivering">
                <div className="qai-da-detail-text">{action.rationale}</div>
              </Section>
            )}

            <Section title="Källa / evidens">
              <Provenance src={action.source} />
              {action.outreach_draft_id && (
                <div className="qai-da-detail-line">
                  Kopplad OutreachDraft #{action.outreach_draft_id}
                  <span className="qai-da-tag-note"> — utkast, ej skickat</span>
                </div>
              )}
            </Section>

            <Section title="Förutsättningar">
              <PrereqList prereqs={action.prerequisites} />
            </Section>

            <Section title="Blockerare">
              <BlockerList blockers={action.blockers} />
            </Section>

            {action.completion_evidence?.length > 0 && (
              <Section title="Bevis för klarmarkering">
                <ul className="qai-da-detail-list">
                  {action.completion_evidence.map((e, i) => (
                    <li key={i}>{e.label || e.kind}</li>
                  ))}
                </ul>
              </Section>
            )}

            <div className="qai-da-detail-controls">
              <Controls action={action} runVerb={runVerb} />
            </div>

            <Section title="Kommentarer">
              <ul className="qai-da-detail-comments">
                {comments.map((c) => (
                  <li key={c.id}>
                    <div className="qai-da-detail-comment-body">{c.body}</div>
                    <div className="qai-da-detail-comment-meta">
                      {new Date(c.created_at).toLocaleString("sv-SE")}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="qai-da-detail-comment-form">
                <textarea
                  className="qai-da-detail-textarea"
                  rows={2}
                  placeholder="Kommentar…"
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                />
                <button
                  type="button"
                  className="qai-da-btn qai-da-btn-primary"
                  onClick={submitComment}
                  disabled={!commentDraft.trim()}
                >
                  Spara
                </button>
              </div>
            </Section>

            <Section title="Historik">
              <ol className="qai-da-detail-history">
                {history.map((e) => (
                  <li key={e.id}>
                    <span className="qai-da-detail-history-kind">
                      {e.event_type}
                    </span>
                    {e.from_status && e.to_status && (
                      <span className="qai-da-detail-history-transition">
                        {" "}({e.from_status} → {e.to_status})
                      </span>
                    )}
                    {e.reason && (
                      <span className="qai-da-detail-history-reason">
                        {" "} — "{e.reason}"
                      </span>
                    )}
                    <span className="qai-da-detail-history-at">
                      {" "} · {new Date(e.created_at).toLocaleString("sv-SE")}
                    </span>
                  </li>
                ))}
              </ol>
            </Section>
          </>
        )}

        <div className="qai-da-detail-footer-note">
          Alla externa handlingar (utskick, distribution, möten) utförs
          manuellt utanför Sigge. Denna vy är arbetsplanering, inte
          exekvering.
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="qai-da-detail-section">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

function MetaGrid({ a }) {
  return (
    <div className="qai-da-detail-meta">
      <MetaRow label="Status" value={a.status} />
      <MetaRow label="Prioritet" value={a.priority} />
      <MetaRow label="Beredskap" value={a.readiness} />
      <MetaRow label="Typ" value={a.action_type} />
      <MetaRow label="Köpare" value={a.acquirer_name || "—"} />
      <MetaRow label="Ägare" value={a.owner_id ? `#${a.owner_id}` : "—"} />
      <MetaRow label="Deadline" value={a.due_at ? new Date(a.due_at).toLocaleDateString("sv-SE") : "—"} />
      <MetaRow label="Källa" value={a.source?.kind || "—"} />
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="qai-da-detail-meta-row">
      <span className="qai-da-detail-meta-label">{label}</span>
      <span className="qai-da-detail-meta-value">{value}</span>
    </div>
  );
}

function Provenance({ src }) {
  if (!src) return <div className="qai-da-detail-line">Ingen registrerad källa.</div>;
  const parts = [];
  if (src.snapshot_id) parts.push(`Snapshot #${src.snapshot_id}`);
  if (src.suggestion_kind) parts.push(`Förslag: ${src.suggestion_kind}`);
  if (src.buyer_intel_item_id) parts.push(`Buyer intel-post #${src.buyer_intel_item_id}`);
  if (src.analysis_id) parts.push(`Analys #${src.analysis_id}`);
  if (src.inbound_message_id) parts.push(`Inkommande meddelande #${src.inbound_message_id}`);
  return (
    <div className="qai-da-detail-line">
      <span className="qai-da-detail-meta-value">{src.kind}</span>
      {parts.length > 0 && (
        <span className="qai-da-detail-provenance"> · {parts.join(" · ")}</span>
      )}
    </div>
  );
}

function PrereqList({ prereqs }) {
  if (!prereqs || prereqs.length === 0) {
    return <div className="qai-da-empty">Inga registrerade förutsättningar.</div>;
  }
  return (
    <ul className="qai-da-detail-list">
      {prereqs.map((p, i) => (
        <li key={i}>
          <span className={p.satisfied ? "qai-da-check-ok" : "qai-da-check-x"}>
            {p.satisfied ? "✓" : "✗"}
          </span>{" "}
          {p.label || p.kind}
        </li>
      ))}
    </ul>
  );
}

function BlockerList({ blockers }) {
  if (!blockers || blockers.length === 0) {
    return <div className="qai-da-empty">Inga blockerare.</div>;
  }
  return (
    <ul className="qai-da-detail-list">
      {blockers.map((b, i) => (
        <li key={i} className={b.resolved ? "qai-da-blocker-resolved" : "qai-da-blocker-open"}>
          {b.resolved ? "✓" : "!"} {b.label || b.kind}
        </li>
      ))}
    </ul>
  );
}

function Controls({ action, runVerb }) {
  const s = action.status;
  const btns = [];
  if (s === "proposed" || s === "pending_review") {
    btns.push(
      <Btn key="approve" kind="primary" onClick={() => runVerb(approveDealAction)}>Godkänn</Btn>,
      <Btn key="reject" onClick={() => {
        const r = window.prompt("Anledning till avslag (valfritt)?") || "";
        runVerb(rejectDealAction, { reason: r });
      }}>Avvisa</Btn>,
    );
    if (s === "proposed") {
      btns.push(
        <Btn key="review" onClick={() => runVerb(sendDealActionForReview)}>Skicka för granskning</Btn>,
      );
    }
  }
  if (s === "approved") {
    btns.push(<Btn key="start" kind="primary" onClick={() => runVerb(startDealAction)}>Starta</Btn>);
  }
  if (s === "in_progress") {
    btns.push(<Btn key="complete" kind="primary" onClick={() => runVerb(completeDealAction)}>Markera klar</Btn>);
  }
  if (s === "approved" || s === "in_progress") {
    btns.push(
      <Btn key="block" onClick={() => {
        const r = window.prompt("Blockeringsorsak?");
        if (r && r.trim()) runVerb(blockDealAction, { reason: r.trim() });
      }}>Blockera</Btn>,
    );
  }
  if (s === "blocked") {
    btns.push(
      <Btn key="unblock" kind="primary" onClick={() => runVerb(resolveDealActionBlockers)}>Lös blockering</Btn>,
    );
  }
  const isOpen = ["proposed", "pending_review", "approved", "in_progress", "blocked"].includes(s);
  if (isOpen) {
    btns.push(<Btn key="cancel" onClick={() => runVerb(cancelDealAction)}>Avbryt</Btn>);
    btns.push(<Btn key="supersede" onClick={() => runVerb(supersedeDealAction)}>Ersätt</Btn>);
  }

  return <div className="qai-da-detail-btns">{btns}</div>;
}

function Btn({ kind = "ghost", onClick, children }) {
  return (
    <button
      type="button"
      className={`qai-da-btn qai-da-btn-${kind}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
